import React from 'react'
import { Box } from '../../atoms/Box'
import { Text } from '../../atoms/Text'
import { Flex } from '../../atoms/Flex'
import { Icon } from '../../atoms/Icon'
import { IconButton } from '../../atoms/IconButton'
import { Badge } from '../../atoms/Badge'
import { styled, useTheme } from 'styled-components'
import type { BuildingPermit } from '@popety-io/types'

export interface SavedBuildingPermitsGridProps {
  permits: BuildingPermit[]
  onPermitClick: (permit: BuildingPermit) => void
  onRemovePermit?: (permitId: string) => void
}

const GridContainer = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small};
`

const PermitCard = styled(Box)`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  transition: all ${({ theme }) => theme.transitions.default};
  cursor: pointer;
  position: relative;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  padding: ${({ theme }) => theme.spacing.medium};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
    border-color: ${({ theme }) => theme.colors.border.default};
  }
`

const RemoveButton = styled(IconButton)`
  position: absolute;
  top: ${({ theme }) => theme.spacing.xsmall};
  right: ${({ theme }) => theme.spacing.xsmall};
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  border-radius: ${({ theme }) => theme.borderRadius.circle};
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all ${({ theme }) => theme.transitions.default};

  ${PermitCard}:hover & {
    opacity: 1;
  }

  &:hover {
    background: white;
    transform: scale(1.1);
  }
`

const PermitBadge = styled(Badge)<{
  $backgroundColor?: string
  $textColor?: string
}>`
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
  ${({ $backgroundColor }) =>
    $backgroundColor && `background-color: ${$backgroundColor};`}
  ${({ $textColor }) => $textColor && `color: ${$textColor};`}
  border: none;
`

const PermitNumber = styled(Text)`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-family: ${({ theme }) => theme.typography.fontFamily.mono};
  margin-bottom: ${({ theme }) => theme.spacing.micro};
`

const AddressText = styled(Text)`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.small};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const DescriptionText = styled(Text)`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-bottom: ${({ theme }) => theme.spacing.small};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const InfoRow = styled(Flex)`
  align-items: center;
  gap: ${({ theme }) => theme.spacing.micro};
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  margin-top: ${({ theme }) => theme.spacing.xsmall};
`

const getCategoryColor = (category: string): { bg: string; text: string } => {
  switch (category) {
    case 'development':
      return { bg: '#dbeafe', text: '#1e40af' } // Blue
    case 'renovation':
      return { bg: '#fef3c7', text: '#92400e' } // Amber
    case 'transformation':
      return { bg: '#e0e7ff', text: '#3730a3' } // Indigo
    case 'elevation':
      return { bg: '#ddd6fe', text: '#5b21b6' } // Violet
    case 'regularisation':
      return { bg: '#f3e8ff', text: '#6b21a8' } // Purple
    default:
      return { bg: '#f3f4f6', text: '#4b5563' } // Gray
  }
}

const getStatusColor = (status: string): { bg: string; text: string } => {
  switch (status) {
    case 'approved':
      return { bg: '#d1fae5', text: '#065f46' } // Green
    case 'pending':
      return { bg: '#fef3c7', text: '#92400e' } // Amber
    case 'rejected':
      return { bg: '#fee2e2', text: '#991b1b' } // Red
    case 'completed':
      return { bg: '#dbeafe', text: '#1e40af' } // Blue
    case 'instruction':
      return { bg: '#e0e7ff', text: '#3730a3' } // Indigo
    case 'enregistrement':
      return { bg: '#f3e8ff', text: '#6b21a8' } // Purple
    default:
      return { bg: '#f3f4f6', text: '#4b5563' } // Gray
  }
}

const formatDate = (date: Date | string | undefined): string => {
  if (!date) {
    return 'N/A'
  }

  try {
    const dateObj = date instanceof Date ? date : new Date(date)

    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return 'N/A'
    }

    return new Intl.DateTimeFormat('fr-CH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(dateObj)
  } catch {
    return 'N/A'
  }
}

export const SavedBuildingPermitsGrid: React.FC<
  SavedBuildingPermitsGridProps
> = ({ permits, onPermitClick, onRemovePermit }) => {
  useTheme()

  const handleCardClick = (permit: BuildingPermit) => {
    onPermitClick(permit)
  }

  const handleRemoveClick = (e: React.MouseEvent, permitId: string) => {
    e.stopPropagation()
    onRemovePermit?.(permitId)
  }

  return (
    <GridContainer>
      {permits.map((permit) => {
        const categoryColor = getCategoryColor(permit.category || '')
        const statusColor = getStatusColor(permit.status || '')

        return (
          <PermitCard key={permit.id} onClick={() => handleCardClick(permit)}>
            {onRemovePermit && (
              <RemoveButton
                onClick={(e) => handleRemoveClick(e, permit.id)}
                aria-label="Remove permit"
              >
                <Icon name="x" />
              </RemoveButton>
            )}

            <Flex gap="4px" wrap="wrap" marginBottom="8px">
              <PermitBadge
                $backgroundColor={categoryColor.bg}
                $textColor={categoryColor.text}
              >
                {permit.category}
              </PermitBadge>
              <PermitBadge
                $backgroundColor={statusColor.bg}
                $textColor={statusColor.text}
              >
                {permit.status}
              </PermitBadge>
            </Flex>

            <PermitNumber>{permit.permitNumber || permit.id}</PermitNumber>

            <AddressText>{permit.address}</AddressText>

            {permit.description && (
              <DescriptionText>{permit.description}</DescriptionText>
            )}

            <Flex direction="column" gap="4px">
              <InfoRow>
                <Icon name="calendar" size="xs" />
                <Text>{formatDate(permit.filingDate)}</Text>
              </InfoRow>

              {permit.owner && (
                <InfoRow>
                  <Icon name="user" size="xs" />
                  <Text>{permit.owner}</Text>
                </InfoRow>
              )}

              {permit.type && (
                <InfoRow>
                  <Icon name="tag" size="xs" />
                  <Text>{permit.type}</Text>
                </InfoRow>
              )}
            </Flex>
          </PermitCard>
        )
      })}
    </GridContainer>
  )
}
